/**
 * SaveSlotsDialog Component
 *
 * Modal dialog for managing server-side save slots.
 * Provides 3 slots for saving and loading workspace state.
 */

import { useState, useEffect, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  X,
  Save,
  Download,
  Trash2,
  Cloud,
  Loader2,
  Pencil,
} from 'lucide-react';
import {
  listSaveSlots,
  loadFromSlot,
  saveToSlot,
  deleteSlot,
  renameSlot,
  applyLoadedSave,
  formatSaveDate,
  type SaveSlotMetadata,
  type SaveSlot,
} from '../services/saveApi';

interface SaveSlotsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SlotStatus = 'idle' | 'saving' | 'loading' | 'deleting' | 'renaming';

export function SaveSlotsDialog({ open, onOpenChange }: SaveSlotsDialogProps) {
  const [slots, setSlots] = useState<(SaveSlotMetadata | null)[]>([
    null,
    null,
    null,
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slotStatus, setSlotStatus] = useState<Record<SaveSlot, SlotStatus>>({
    1: 'idle',
    2: 'idle',
    3: 'idle',
  });
  const [editingSlot, setEditingSlot] = useState<SaveSlot | null>(null);
  const [editName, setEditName] = useState('');

  // Fetch slots when dialog opens
  useEffect(() => {
    if (open) {
      fetchSlots();
    }
  }, [open]);

  const fetchSlots = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await listSaveSlots();
      setSlots(response.slots);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch save slots'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = useCallback(async (slot: SaveSlot) => {
    setSlotStatus((prev) => ({ ...prev, [slot]: 'saving' }));
    setError(null);

    try {
      const metadata = await saveToSlot(slot);
      setSlots((prev) => {
        const newSlots = [...prev];
        newSlots[slot - 1] = metadata;
        return newSlots;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSlotStatus((prev) => ({ ...prev, [slot]: 'idle' }));
    }
  }, []);

  const handleLoad = useCallback(
    async (slot: SaveSlot) => {
      setSlotStatus((prev) => ({ ...prev, [slot]: 'loading' }));
      setError(null);

      try {
        const saveData = await loadFromSlot(slot);
        applyLoadedSave(saveData);
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setSlotStatus((prev) => ({ ...prev, [slot]: 'idle' }));
      }
    },
    [onOpenChange]
  );

  const handleDelete = useCallback(async (slot: SaveSlot) => {
    if (!confirm('Are you sure you want to delete this save?')) {
      return;
    }

    setSlotStatus((prev) => ({ ...prev, [slot]: 'deleting' }));
    setError(null);

    try {
      await deleteSlot(slot);
      setSlots((prev) => {
        const newSlots = [...prev];
        newSlots[slot - 1] = null;
        return newSlots;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setSlotStatus((prev) => ({ ...prev, [slot]: 'idle' }));
    }
  }, []);

  const handleRename = useCallback(
    async (slot: SaveSlot, name: string) => {
      const trimmedName = name.trim();
      const slotData = slots[slot - 1];
      const currentDisplayName = slotData?.customName ?? slotData?.sceneName;

      // Skip API call if name is empty or unchanged
      if (!trimmedName || trimmedName === currentDisplayName) {
        setEditingSlot(null);
        return;
      }

      setSlotStatus((prev) => ({ ...prev, [slot]: 'renaming' }));
      setError(null);

      try {
        const metadata = await renameSlot(slot, trimmedName);
        setSlots((prev) => {
          const newSlots = [...prev];
          newSlots[slot - 1] = metadata;
          return newSlots;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to rename');
      } finally {
        setSlotStatus((prev) => ({ ...prev, [slot]: 'idle' }));
        setEditingSlot(null);
      }
    },
    [slots]
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-xl">
          <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-white">
            <Cloud className="h-5 w-5 text-blue-400" />
            Cloud Save Slots
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-gray-400">
            Save your work to the server. 3 slots available.
          </Dialog.Description>

          {/* Error message */}
          {error && (
            <div className="mt-3 rounded-md bg-red-900/50 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          {/* Loading state */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
              <span className="ml-2 text-gray-400">Loading slots...</span>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {([1, 2, 3] as SaveSlot[]).map((slotNum) => {
                const slotData = slots[slotNum - 1];
                const status = slotStatus[slotNum];
                const isBusy = status !== 'idle';
                const isEditing = editingSlot === slotNum;
                const displayName = slotData
                  ? (slotData.customName ?? slotData.sceneName)
                  : null;

                return (
                  <div
                    key={slotNum}
                    className="flex items-center gap-3 rounded-lg border border-gray-700 bg-gray-800 p-3"
                  >
                    {/* Slot number badge */}
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-700 text-sm font-bold text-gray-300">
                      {slotNum}
                    </div>

                    {/* Slot info */}
                    <div className="flex-1 min-w-0">
                      {slotData ? (
                        <>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleRename(slotNum, editName);
                                } else if (e.key === 'Escape') {
                                  setEditingSlot(null);
                                }
                              }}
                              onBlur={() => handleRename(slotNum, editName)}
                              autoFocus
                              className="w-full truncate bg-transparent font-medium text-white outline-none border-b border-blue-400"
                              maxLength={50}
                            />
                          ) : (
                            <div className="truncate font-medium text-white">
                              {displayName}
                            </div>
                          )}
                          <div className="text-xs text-gray-400">
                            {formatSaveDate(slotData.savedAt)} &middot;{' '}
                            {slotData.tabCount} tab
                            {slotData.tabCount !== 1 ? 's' : ''},{' '}
                            {slotData.toolCount} tool
                            {slotData.toolCount !== 1 ? 's' : ''}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 italic">
                          Empty slot
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      {/* Rename button (only if slot has data) */}
                      {slotData && (
                        <button
                          onClick={() => {
                            setEditingSlot(slotNum);
                            setEditName(
                              slotData.customName ?? slotData.sceneName
                            );
                          }}
                          disabled={isBusy}
                          className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-600 text-white transition-colors hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Rename this save"
                        >
                          {status === 'renaming' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Pencil className="h-4 w-4" />
                          )}
                        </button>
                      )}

                      {/* Save button */}
                      <button
                        onClick={() => handleSave(slotNum)}
                        disabled={isBusy}
                        className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Save to this slot"
                      >
                        {status === 'saving' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </button>

                      {/* Load button (only if slot has data) */}
                      {slotData && (
                        <button
                          onClick={() => handleLoad(slotNum)}
                          disabled={isBusy}
                          className="flex h-8 w-8 items-center justify-center rounded-md bg-green-600 text-white transition-colors hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Load from this slot"
                        >
                          {status === 'loading' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                      )}

                      {/* Delete button (only if slot has data) */}
                      {slotData && (
                        <button
                          onClick={() => handleDelete(slotNum)}
                          disabled={isBusy}
                          className="flex h-8 w-8 items-center justify-center rounded-md bg-red-600 text-white transition-colors hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete this save"
                        >
                          {status === 'deleting' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Close button */}
          <Dialog.Close asChild>
            <button
              className="absolute right-4 top-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
